import { appVisibilityChange } from '@wix/da-shared-react/pkg/redux/app/actions';
import {
  all,
  delay,
  getContext,
  put,
  select,
  takeLatest,
  throttle,
} from 'redux-saga/effects';
import {
  isValidStateTransition,
  updateRoomStateForFocusChange,
} from '../../helpers/roomState';
import { getActiveRoomId } from '../../selectors/room';
import { getRoomUnreadState } from '../../selectors/roomUnread';
import {
  markLastViewedMessage,
  markLastViewedMessageSuccess,
  updateRoomUnreadState,
} from '../../actions/roomUnread';
import {
  fetchRoomUnreadState,
  setRoomUnreadState,
  markRoomRead,
} from '../../actions/room';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@wix/da-papi-types/lib/supabase';
import ddt from '@wix/da-ddt';

/**
 * Handle window losing/gaining focus
 */
function* handleAppVisibilityChange(
  action: ReturnType<typeof appVisibilityChange>
) {
  const { hidden } = action.payload;
  const activeRoomId = yield select(getActiveRoomId);

  if (!activeRoomId) {
    return; // No active room, nothing to update
  }

  const roomState = yield select(getRoomUnreadState, activeRoomId);

  if (!roomState) {
    return; // No room state, nothing to update
  }

  const updates = updateRoomStateForFocusChange(roomState, !hidden);

  // Validate the state transition
  const newWindowState = updates.windowState ?? roomState.windowState;
  if (isValidStateTransition(roomState.windowState, newWindowState)) {
    ddt.log(
      'damz.unread',
      'Updating room state after focus change',
      activeRoomId,
      updates
    );
    yield put(updateRoomUnreadState(activeRoomId, updates));

    // If gaining focus, mark as read
    if (!hidden) {
      yield delay(1000);
      yield put(markRoomRead(activeRoomId));
    }
  }
}

/**
 * Async fetch the unread state from server on opening of a room
 */
function* handleFetchRoomUnreadState(
  action: ReturnType<typeof fetchRoomUnreadState>
) {
  const { room_id } = action.payload;
  try {
    const client: SupabaseClient<Database, 'damz'> =
      yield getContext('supaClient');
    const response: {
      data: { unread_count: number; last_viewed_message_id: string }[];
    } = yield client.rpc('get_room_unread_state', {
      p_room_id: room_id,
    });

    if (response?.data && response.data.length > 0) {
      const unreadState = response.data[0];
      yield put(
        setRoomUnreadState(
          room_id,
          unreadState.last_viewed_message_id,
          unreadState.unread_count
        )
      );
    }
  } catch (err) {
    ddt.error('damz.unread', 'handleFetchRoomUnreadState failed', err);
  }
}

function* handleMarkLastViewed(
  action: ReturnType<typeof markLastViewedMessage>
) {
  yield delay(500);
  const { room_id, messageId } = action.payload;

  // Check if this message was already marked as viewed
  const roomState = yield select(getRoomUnreadState, room_id);
  if (roomState?.lastMarkedMessageId === messageId) {
    return;
  }

  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');

  try {
    yield client.rpc('mark_last_seen', {
      p_room_id: room_id,
      p_msg_id: messageId,
    });

    // Mark as successfully processed
    yield put(markLastViewedMessageSuccess(room_id, messageId));
  } catch (error) {
    // Log error but don't throw to avoid breaking the saga
    ddt.error('damz.unread', 'Failed to mark last viewed message', error);
  }
}

/**
 * Unread room state saga - handles unread room state management
 */
export function* roomUnreadSaga() {
  yield all([
    takeLatest(appVisibilityChange, handleAppVisibilityChange),
    throttle(500, fetchRoomUnreadState, handleFetchRoomUnreadState),
    takeLatest(markLastViewedMessage, handleMarkLastViewed),
  ]);
}
